import { Formik } from "formik";
import i18next from "i18next";
import { RecordTypeEnum } from "src/api/models/Enums";
import { ReactSelectOption } from "src/types";
import { Button, Card, Col, Form, Nav, Row } from 'react-bootstrap';
import Select from 'react-select';
import _ from 'lodash';
import { createReportValidationSchema } from "./createReportValidationSchema";
import RequiredFormLabel from "../RequiredFormLabel/RequiredFormLabel";
import SpinnerButton from "../SpinnerButton/SpinnerButton";
import { CreateReportDto } from ".";
import MotornetVehicleSelector, { MotornetSelectedVehicle, getMakeName } from "../../motornet/common/MotornetVehicleSelector";
import { productionFetchParams } from "../../motornet/common/selectorMeta";
import { useMemo, useState } from "react";
import { createVehicleSearch } from "src/vehicleList/vehicleSearch";
import MultiFileUploader from "../MultiFileUploader/MultiFileUploader";
import VehicleCardBody from "../VehicleCardBody/VehicleCardBody";
import AsyncSelect from "react-select/async";
import SelectOptWithSubtitle from "../SelectOptWithSubtitle/SelectOptWithSubtitle";
import { useApiClient } from "src/hooks/useApiClient";
import { VehicleEntity } from "src/api";
import { useErrorToast } from "src/hooks/useErrorToast";
import { randomString } from "src/utils";
import { Storage } from 'aws-amplify';
import { useAuth } from "src/hooks/useAuth";
import { useAccountTypes } from "src/hooks/useAccountTypes";
import toaster from "src/utils/toaster";
// @ts-ignore
import './reportForm.scss';

type ReportFormProps = {
  submitFn: any
  handleClose?: (reload?: boolean) => void
}

const SUPPORTED_FILE_TYPES = [
  'pdf',
  'doc',
  'docx',
  'ppt',
  'pptx',
];

type CreateReportFormData = Partial<CreateReportDto & {
  vehicle: any
}>

const buildInitialValues = ({ user, userReferencePartnerList }): CreateReportFormData => {
  return ({
    RecordTypeId: '',
    RecordType: '',
    FirstName: '',
    LastName: '',
    Company: '',
    Partita_IVA__c: '',
    Codice_Fiscale__c: '',
    Email__c: '',
    Phone: '',
    Marca__c: '',
    Modello__c: '',
    ID_Vehicle_HMP__c: '',
    signedAttachments: [],
    Messaggio__c: '',
    vehicle: {},
  })
}


export const ReportForm = ({ submitFn, handleClose }: ReportFormProps) => {
  const { accountTypes } = useAccountTypes()
  const [uploadFiles, setUploadFiles] = useState<File[]>([])
  const { user, userReferencePartnerList } = useAuth()
  const initialValues: Partial<CreateReportFormData> = buildInitialValues({
    user,
    userReferencePartnerList
  });
  const [vehicleSource, setVehicleSource] = useState<string>('hmp')
  const [vehicleSearchOpt, setVehicleSearchOpt] = useState<ReactSelectOption | null>(null)
  const { apiClient, handleApiResponse, handleApi } = useApiClient();
  const [pendingCreation, setPendingCreation] = useState(false);

  const { showError } = useErrorToast();

  const uploadFilesToBucket = async () => {
    if (!uploadFiles.length) {
      return [];
    }

    const filesToUploadList: any[] = uploadFiles.map(file => {
      const ext = file.name.split('.').pop()
      const key = `opportunity_uploads/${randomString(10)}.${ext}`
      return { file, filename: file.name, key, fileType: ext }
    })

    const uploadOps: any[] = filesToUploadList.map(obj => {
      return Storage.put(obj.key, obj.file, {
        contentType: obj.file.type,
        bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_SALESFORCE_IMPORT,
      });
    })

    try {
      await Promise.all(uploadOps)
    } catch (e) {
      showError(e, i18next.t('error_loading_file'))
      throw e;
    }

    return filesToUploadList.map(el => ({ filename: el.filename, key: `public/${el.key}`, fileType: '' }))
  }

  const buildPayload = async (values: CreateReportFormData) => {
    const signedAttachments = await uploadFilesToBucket();

    return ({
      RecordTypeId: values.RecordTypeId || null,
      RecordType: values.RecordType || null,
      FirstName: values.FirstName || null,
      LastName: values.LastName || null,
      Company: values.Company || null,
      Partita_IVA__c: values.Partita_IVA__c || null,
      Codice_Fiscale__c: values.Codice_Fiscale__c || null,
      Email__c: values.Email__c || null,
      Phone: values.Phone || null,
      Marca__c: values.Marca__c || null,
      Modello__c: values.Modello__c || null,
      ID_Vehicle_HMP__c: values.ID_Vehicle_HMP__c || null,
      signedAttachments: signedAttachments || [],
      Messaggio__c: values.Messaggio__c || null,
      Noleggiatore__c: values.Noleggiatore__c || null,
    })
  }

  const handleFormSubmit = async (values, formikHelpers) => {
    setPendingCreation(true);
    const payload = await buildPayload(values);
    handleApiResponse(apiClient.salesforceOpportunityController.createNewReport(payload))
      .then((res) => {
        toaster.success(i18next.t('report_created_successfully'));
        if (!!res?.warning) toaster.warning(res.warning);
        handleClose?.(true)
      })
      .catch(error => {
        showError(error, i18next.t('report_creation_failed'));
      }).finally(() => {
        setPendingCreation(false);
      });
  }

  const [selectedVehicle, setSelectedVehicle] = useState<MotornetSelectedVehicle>({
    brand: null,
    model: null,
    version: null,
  });
  const [previewImages, setPreviewImages] = useState<string[]>([]);

  // Motornet images live on the full vehicle, keyed by the version's eurotax code.
  const fetchVehicleImages = async (version: any) => {
    const eurotax = version?.data?.codiceEurotax;
    if (!eurotax) {
      setPreviewImages([]);
      return;
    }
    try {
      const vehicleKind = (selectedVehicle.brand as any)?.data?.vehicleKind ?? 'auto';
      const payload = await apiClient.motornetControllerService.getMotornetVehicle(eurotax, vehicleKind, productionFetchParams(version?.data, selectedVehicle.model?.data));
      setPreviewImages((payload?.images ?? []).map((img: any) => img?.url).filter(Boolean));
    } catch (error) {
      console.warn('Failed to fetch motornet vehicle images', error);
      setPreviewImages([]);
    }
  };

  const setVehicleSearch = (opt: any, setFieldValue) => {
    setVehicleSearchOpt(opt)
    setFieldValue('vehicle', null);
    setFieldValue('Marca__c', '');
    setFieldValue('Modello__c', '');

    // FETCH THE VEHICLE SELECTED FROM THE DROPDOWN
    handleApiResponse(apiClient.vehicleController.getVehicles1(opt.vehicle.id))
      .then((res: VehicleEntity) => {
        setFieldValue('ID_Vehicle_HMP__c', res.id);
        setFieldValue('vehicle', res);
        setFieldValue('Marca__c', res.dati_veicolo?.marca);
        setFieldValue('Modello__c', res.dati_veicolo?.modello);
        setFieldValue('targa', res.dettagli_veicolo?.targa);
        setFieldValue('telaio', res.dettagli_veicolo?.telaio);
        setFieldValue('dataFineContrattoDriveItEasy', res.stato_corrente?.dataFineContrattoDriveItEasy);
      })
      .catch(error => {
        console.error('Error fetching vehicle details: ', error);
      });
  }

  const vehicleSearch = useMemo(
    () =>
      createVehicleSearch(
        (params) => apiClient.motornetControllerService.getPlatformVehicles(params),
        process.env.REACT_APP_QUICKO_ENABLED === '1' ? { quotable: true } : {}
      ),
    [apiClient]
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={createReportValidationSchema}
      onSubmit={handleFormSubmit}
      enableReinitialize={true}
      validateOnBlur
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        setFieldValue,
        setTouched,
      }) => {
        const isPrivato = values?.RecordTypeId === RecordTypeEnum.Privato;
        const isLiberoProfessionista = values?.RecordTypeId === RecordTypeEnum.Libero_Professionista;
        const isAzienda = values?.RecordTypeId === RecordTypeEnum.Azienda;

        return (
          <Form id="report-form" onSubmit={handleSubmit}>
            <Row className="fields-grid">
              <Form.Group className="mb-3">
                <RequiredFormLabel
                  label={i18next.t('type')}
                  required={!values.RecordTypeId}
                />
                <Select
                  menuPosition="fixed"
                  options={accountTypes}
                  onChange={(selected) => {
                    setFieldValue(
                      'RecordTypeId',
                      selected ? selected.value : null
                    );
                    setFieldValue(
                      'RecordType',
                      selected ? selected.code : null
                    );
                  }}
                  value={
                    accountTypes?.find(
                      (option) => (values.RecordTypeId ?? '') === option.value
                    ) || null
                  }
                  className={`custom-select ${touched.RecordTypeId && errors.RecordTypeId
                    ? 'select-error'
                    : ''
                    }`}
                />
                {touched.RecordTypeId && errors.RecordTypeId ? (
                  <div style={{ color: 'red' }}>{errors.RecordTypeId}</div>
                ) : null}
              </Form.Group>
              {!isAzienda && (
                <>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel
                      label={i18next.t('first_name')}
                      required={(isPrivato || isLiberoProfessionista) && !values.FirstName}
                    />
                    <Form.Control
                      type="text"
                      name="FirstName"
                      onChange={handleChange}
                      value={values.FirstName ?? ''}
                      isInvalid={!!errors.FirstName && touched.FirstName}
                    />
                    {touched.FirstName && errors.FirstName ? (
                      <div style={{ color: 'red' }}>{errors.FirstName}</div>
                    ) : null}
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel
                      label={i18next.t('last_name')}
                      required={(isPrivato || isLiberoProfessionista) && !values.LastName}
                    />
                    <Form.Control
                      type="text"
                      name="LastName"
                      onChange={handleChange}
                      value={values.LastName ?? ''}
                      isInvalid={!!errors.LastName && touched.LastName}
                    />
                    {touched.LastName && errors.LastName ? (
                      <div style={{ color: 'red' }}>{errors.LastName}</div>
                    ) : null}
                  </Form.Group>
                </>
              )}
              {isAzienda && (
                <Form.Group className="mb-3">
                  <RequiredFormLabel
                    label={i18next.t('company')}
                    required={isAzienda && !values.Company}
                  />
                  <Form.Control
                    type="text"
                    name="Company"
                    onChange={handleChange}
                    value={values.Company ?? ''}
                    isInvalid={!!errors.Company && touched.Company}
                  />
                  {touched.Company && errors.Company ? (
                    <div style={{ color: 'red' }}>{errors.Company}</div>
                  ) : null}
                </Form.Group>
              )}
              {isPrivato && (
                <Form.Group className="mb-3">
                  <RequiredFormLabel
                    label={i18next.t('fiscal_code')}
                  />
                  <Form.Control
                    type="text"
                    name="Codice_Fiscale__c"
                    onChange={handleChange}
                    value={values.Codice_Fiscale__c || ''}
                    isInvalid={
                      !!errors.Codice_Fiscale__c &&
                      touched.Codice_Fiscale__c
                    }
                  />
                  {touched.Codice_Fiscale__c && errors.Codice_Fiscale__c ? (
                    <div style={{ color: 'red' }}>
                      {errors.Codice_Fiscale__c}
                    </div>
                  ) : null}
                </Form.Group>
              )}
              {isLiberoProfessionista && (
                <Form.Group className="mb-3">
                  <RequiredFormLabel
                    label={i18next.t('business_fiscal_code')}
                  />
                  <Form.Control
                    type="text"
                    name="Partita_IVA__c"
                    onChange={handleChange}
                    value={values.Partita_IVA__c || ''}
                    isInvalid={!!errors.Partita_IVA__c && touched.Partita_IVA__c}
                  />
                  {touched.Partita_IVA__c && errors.Partita_IVA__c ? (
                    <div style={{ color: 'red' }}>
                      {errors.Partita_IVA__c}
                    </div>
                  ) : null}
                </Form.Group>
              )}
              {isAzienda && (
                <Form.Group className="mb-3">
                  <RequiredFormLabel
                    label={i18next.t('business_fiscal_code')}
                  />
                  <Form.Control
                    type="text"
                    name="Partita_IVA__c"
                    onChange={handleChange}
                    value={values.Partita_IVA__c || ''}
                    isInvalid={!!errors.Partita_IVA__c && touched.Partita_IVA__c}
                  />
                  {touched.Partita_IVA__c && errors.Partita_IVA__c ? (
                    <div style={{ color: 'red' }}>
                      {errors.Partita_IVA__c}
                    </div>
                  ) : null}
                </Form.Group>
              )}
              <Form.Group className="mb-3">
                <RequiredFormLabel
                  label={i18next.t('email')}
                  required={!values.Email__c}
                />
                <Form.Control
                  type="email"
                  name="Email__c"
                  onChange={handleChange}
                  value={values.Email__c ?? ''}
                  isInvalid={!!errors.Email__c && touched.Email__c}
                />
                {touched.Email__c && errors.Email__c ? (
                  <div style={{ color: 'red' }}>{errors.Email__c}</div>
                ) : null}
              </Form.Group>
              <Form.Group className="mb-3">
                <RequiredFormLabel
                  label={i18next.t('mobile_phone')}
                  required={!values.Phone}
                />
                <Form.Control
                  type="text"
                  name="Phone"
                  onChange={handleChange}
                  value={values.Phone || ''}
                  isInvalid={!!errors.Phone && touched.Phone}
                />
                {touched.Phone && errors.Phone ? (
                  <div style={{ color: 'red' }}>{errors.Phone}</div>
                ) : null}
              </Form.Group>
            </Row>
            <Row>
              <Col sm={12}>
                <Form.Group className="mb-3">
                  <Form.Label>{i18next.t('notes')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="Messaggio__c"
                    onChange={handleChange}
                    value={values.Messaggio__c || ''}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className='mb-3 mt-3'>
              <Col>
                <RequiredFormLabel
                  label={i18next.t('select_vehicle')}
                  required={!values.Modello__c && !values.Marca__c}
                />
                <Nav variant='tabs' activeKey={vehicleSource} onSelect={(k) => {
                  setTouched({ ...touched, Marca__c: true, Modello__c: true });
                  setFieldValue('vehicle', null);
                  setFieldValue('ID_Vehicle_HMP__c', '');
                  setFieldValue('Marca__c', '');
                  setFieldValue('Modello__c', '');
                  setVehicleSearchOpt(null)
                  setSelectedVehicle({ brand: null, model: null, version: null })
                  setPreviewImages([])
                  setVehicleSource(k || 'hmp')
                }}>
                  <Nav.Item>
                    <Nav.Link eventKey="hmp">	{i18next.t('vehicle_hmp')}</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="external">	{i18next.t('vehicle_external')}</Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
            </Row>

            {/* SELECT VEHICLE */}
            <Row>
              {vehicleSource === 'hmp' && (
                <Col md={6}>
                  <Form.Group>
                    <AsyncSelect
                      menuPosition="fixed"
                      placeholder={i18next.t('select')}
                      value={vehicleSearchOpt}
                      noOptionsMessage={() => i18next.t('vehicle_search_placeholder', { defaultValue: 'Ordine, telaio, targa o codice costruttore' })}
                      defaultOptions={vehicleSearchOpt ? [vehicleSearchOpt] : []}
                      loadOptions={vehicleSearch}
                      onChange={(item) => setVehicleSearch(item, setFieldValue)}
                      components={{
                        Option: (props: any) => {
                          return <SelectOptWithSubtitle {...props}></SelectOptWithSubtitle>
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
              )}

              {vehicleSource === 'external' && (
                <MotornetVehicleSelector
                  selectedVehicle={selectedVehicle}
                  onBrandChange={(brand) => {
                    setSelectedVehicle({ brand, model: null, version: null })
                    setPreviewImages([])
                    setFieldValue('Marca__c', getMakeName(brand))
                    setFieldValue('Modello__c', '')
                  }}
                  onModelChange={(model) => {
                    setSelectedVehicle((prev) => ({ ...prev, model, version: null }))
                    setPreviewImages([])
                    const modelName = model?.data?.gammaModello?.descrizione || model?.label || ''
                    setFieldValue('Modello__c', modelName)
                  }}
                  onVersionChange={(version) => {
                    setSelectedVehicle((prev) => ({ ...prev, version }))
                    fetchVehicleImages(version)
                  }}
                />
              )}
              {(!values.Marca__c && touched.Marca__c && errors.Marca__c) ||
                (!values.Modello__c && touched.Modello__c && errors.Modello__c) ? (
                <div style={{ color: 'red' }}>
                  {errors.Marca__c || errors.Modello__c}
                </div>
              ) : null}
            </Row>

            <Card className='mt-2'>
              {!_.isEmpty(values.vehicle) ? (
                <VehicleCardBody data={values.vehicle} />
              ) : (previewImages.length > 0 ?
                <VehicleCardBody data={{
                  dati_veicolo: {
                    marca: getMakeName(selectedVehicle.brand),
                    modello: selectedVehicle.model?.data?.gammaModello?.descrizione || selectedVehicle.model?.label
                  },
                  image_list: previewImages.map(el => ({ url: el }))
                }} />
                : null)}
            </Card>

            <Row className='mt-3'>
              <MultiFileUploader
                files={uploadFiles}
                setFiles={setUploadFiles}
                types={SUPPORTED_FILE_TYPES}
              ></MultiFileUploader>
            </Row>

            <Row className="mt-4">
              <Col md={12} className="d-flex justify-content-end">
                <Button
                  variant="primary"
                  type="submit"
                  className="me-2"
                  // onClick={() => {
                  //   console.log(values)
                  //   console.log(createReportValidationSchema.validateSync(values));
                  // }}
                  disabled={pendingCreation}
                >
                  <SpinnerButton
                    busy={pendingCreation}
                    label={i18next.t('save')}
                  />
                </Button>

                <Button onClick={() => handleClose?.()} disabled={pendingCreation}>
                  <SpinnerButton label={i18next.t('close')} />
                </Button>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  )
}
